import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  useDailyBrief,
  useDeferStep,
  type DailyBriefItem,
} from "../../hooks/useDailyBrief";

interface Props {
  workspaceId?: string | null;
}

const TYPE_GLYPH: Record<string, string> = {
  decision: "◆",
  comm: "✉",
  query: "?",
  micro_task: "◦",
  wait: "⌛",
  milestone: "◇",
  instrumentation: "⟳",
};

const DEFER_REASON_LABEL: Record<string, string> = {
  no_due_date: "No due date",
  low_urgency: "Low urgency",
};

export function DailyBriefTab({ workspaceId }: Props) {
  const navigate = useNavigate();
  const { data, isLoading } = useDailyBrief(workspaceId ?? null);
  const defer = useDeferStep();

  const brief = data?.brief;
  const items = brief?.items ?? [];
  const awaitingReview = brief?.awaitingReview ?? [];
  const load = brief?.load;
  const deferSuggestions = brief?.deferSuggestions ?? [];

  const loadLabel = useMemo(() => {
    if (!load) return "Loading…";
    if (load.heavy) return "Heavy";
    const ratio = load.totalMinutes / load.capacityMinutes;
    if (ratio > 0.6) return "Standard";
    return "Light";
  }, [load]);

  const dayLabel = useMemo(() => {
    return new Date().toLocaleDateString(undefined, {
      weekday: "long",
      month: "short",
      day: "numeric",
    });
  }, []);

  const openStep = (it: DailyBriefItem) => {
    // Focused review/decision page — full context, comment, decision buttons.
    // Falls back to play workspace if for some reason the step has no
    // open card (the TaskReviewPage handles that "view only" case too).
    navigate(`/workspaces/${it.workspaceId}/tasks/${it.stepId}`);
  };

  const onDefer = (e: React.MouseEvent, stepId: string) => {
    e.stopPropagation();
    defer.mutate(stepId);
  };

  if (isLoading) {
    return (
      <div data-testid="daily-brief-loading" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {[0, 1, 2].map((i) => (
          <div key={i} style={{ height: 56, borderRadius: 8, background: "var(--bg-soft)", opacity: 0.6 }} />
        ))}
      </div>
    );
  }

  if (items.length === 0 && awaitingReview.length === 0) {
    return (
      <div data-testid="daily-brief">
        <div className="db-header">
          <div className="greet">Your day — {dayLabel}</div>
          <span className={`pill`} data-testid="daily-brief-load">{loadLabel}</span>
        </div>
        <div className="ho-empty" data-testid="daily-brief-empty">
          Nothing on your plate today. Take a breath.
        </div>
      </div>
    );
  }

  const capacityPct = load
    ? Math.min(100, Math.round((load.totalMinutes / load.capacityMinutes) * 100))
    : 0;

  return (
    <div data-testid="daily-brief">
      <div className="db-header">
        <div className="greet">Your day — {dayLabel}</div>
        <span
          className={`pill ${load?.heavy ? "amber" : ""}`}
          data-testid="daily-brief-load"
        >
          {loadLabel}
        </span>
      </div>
      <div className="db-capacity" data-testid="daily-brief-capacity">
        <div className="db-capacity-bar">
          <div
            className="db-capacity-fill"
            style={{ width: `${capacityPct}%` }}
          />
        </div>
        <div className="db-capacity-meta">
          {load?.totalMinutes ?? 0} / {load?.capacityMinutes ?? 0} min
          {brief && brief.awaitingReviewCount > 0 ? (
            <> · {brief.awaitingReviewCount} awaiting your review</>
          ) : null}
        </div>
      </div>

      {items.length > 0 && (
      <div className="db-section">
        <h3 className="db-section-h">Do first</h3>
        <div className="st-list" data-testid="daily-brief-items">
          {items.map((it) => (
            <div
              key={it.stepId}
              className="st-row"
              data-testid={`daily-brief-row-${it.stepId}`}
              onClick={() => openStep(it)}
            >
              <div className="st-l">
                <span className={`pd-step-type pd-type-${it.type}`}>
                  {TYPE_GLYPH[it.type] ?? "•"} {it.type}
                </span>
                <span className="db-score" title={describeScore(it)}>
                  {it.score}
                </span>
              </div>
              <div className="st-b">
                <div className="st-s">{it.title}</div>
                <div className="st-p">
                  {it.estimatedDurationMinutes} min
                  {it.dueAt ? ` · due ${formatDue(it.dueAt)}` : ""}
                </div>
              </div>
              <div className="st-r" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  className="btn"
                  data-testid={`daily-brief-open-${it.stepId}`}
                  onClick={() => openStep(it)}
                >
                  Open
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      )}

      {awaitingReview.length > 0 && (
        <div className="db-section" data-testid="daily-brief-awaiting">
          <h3 className="db-section-h">Awaiting your review</h3>
          <div className="st-list">
            {awaitingReview.map((it) => (
              <div
                key={it.stepId}
                className="st-row"
                data-testid={`daily-brief-review-${it.stepId}`}
                onClick={() => openStep(it)}
              >
                <div className="st-l">
                  <span className={`pd-step-type pd-type-${it.type}`}>
                    {TYPE_GLYPH[it.type] ?? "•"} {it.type}
                  </span>
                </div>
                <div className="st-b">
                  <div className="st-s">{it.title}</div>
                  <div className="st-p">
                    {it.dueAt ? `due ${formatDue(it.dueAt)}` : "review now"}
                  </div>
                </div>
                <div className="st-r" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className="btn primary"
                    onClick={() => openStep(it)}
                  >
                    Review
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {deferSuggestions.length > 0 ? (
        <div className="db-section">
          <h3 className="db-section-h">Defer suggestions</h3>
          <div className="db-defer" data-testid="daily-brief-defer">
            {deferSuggestions.map((s) => {
              const item = items.find((i) => i.stepId === s.stepId);
              if (!item) return null;
              return (
                <div
                  key={s.stepId}
                  className="st-row"
                  data-testid={`daily-brief-defer-${s.stepId}`}
                >
                  <div className="st-b">
                    <div className="st-s">{item.title}</div>
                    <div className="st-p">
                      {DEFER_REASON_LABEL[s.reason] ?? s.reason}
                    </div>
                  </div>
                  <div className="st-r">
                    <button
                      type="button"
                      className="btn"
                      data-testid={`daily-brief-defer-btn-${s.stepId}`}
                      onClick={(e) => onDefer(e, s.stepId)}
                      disabled={defer.isPending}
                    >
                      Defer to tomorrow
                    </button>
                    <button
                      type="button"
                      className="btn"
                      data-testid={`daily-brief-keep-${s.stepId}`}
                    >
                      Keep on today
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function describeScore(it: DailyBriefItem): string {
  return `urgency ${it.components.urgency} · blocker ${it.components.blockerImpact} · routine ${it.components.playPriority} · supervisor ${it.components.cxoAttention}`;
}

function formatDue(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}
